// src/pages/QuestionListPage.jsx
// re-push trigger
import React, { useEffect, useMemo, useState } from 'react';
import BackButton from '../components/BackButton';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  IconButton,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const QuestionListPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters / search / sort / pagination
  const [lessonFilter, setLessonFilter] = useState(''); // now holds lesson id or '' for all
  const [expirationFilter, setExpirationFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // lessons list for dropdown
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  // sorting: orderBy can be 'createdAt' or 'expirationDate' or 'id'
  const [orderBy, setOrderBy] = useState('createdAt');
  const [orderDir, setOrderDir] = useState('desc'); // 'asc' | 'desc'

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const token = localStorage.getItem('teacherToken');

  useEffect(() => {
    let mounted = true;

    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const res = await axios.get('https://education-backend.liara.run/api/question/my-questions', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!mounted) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.items ?? []);
        const normalized = (data || []).map(q => ({
          ...q,
          createdAt: q.createdAt ?? q.created_at ?? q.created ?? null,
          expirationDate: q.expirationDate ?? q.expiration_date ?? q.expire ?? null
        }));
        setQuestions(normalized);
      } catch (err) {
        console.error('خطا در دریافت سؤال‌ها:', err);
        setQuestions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const fetchLessons = async () => {
      setLessonsLoading(true);
      try {
        const res = await axios.get('https://education-backend.liara.run/api/lesson/by-teacher', {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: s => s < 500
        });
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.items ?? []);
        // normalize lesson objects to { id, title }
        const normalized = (arr || []).map(l => ({
          id: l.id ?? l._id ?? l.Id,
          title: l.title ?? l.Title ?? l.name ?? `درس ${l.id ?? ''}`
        }));
        setLessons(normalized);
      } catch (err) {
        console.error('خطا در دریافت درس‌ها:', err);
        setLessons([]);
      } finally {
        if (mounted) setLessonsLoading(false);
      }
    };

    fetchQuestions();
    fetchLessons();

    return () => { mounted = false; };
  }, [token]);

  // handlers
  const handleEdit = (id) => navigate(`/edit-question/${id}`);

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این سؤال مطمئن هستید؟')) return;
    try {
      const res = await fetch(`https://education-backend.liara.run/api/question/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setQuestions(prev => prev.filter(q => q.id !== id));
        alert('سؤال حذف شد.');
      } else {
        const text = await res.text();
        alert('خطا در حذف: ' + text);
      }
    } catch (err) {
      console.error(err);
      alert('خطا در ارتباط با سرور.');
    }
  };

  // sorting helper
  const comparator = (a, b) => {
    const getVal = (item, key) => {
      if (!item) return null;
      const v = item[key];
      if (!v) return null;
      const n = Date.parse(String(v));
      return isNaN(n) ? String(v).toLowerCase() : n;
    };
    const va = getVal(a, orderBy);
    const vb = getVal(b, orderBy);
    if (va === vb) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return (va < vb ? -1 : 1) * (orderDir === 'asc' ? 1 : -1);
  };

  // processed list: filtered + searched + sorted
  const processed = useMemo(() => {
    let arr = [...questions];

    // search by content or lessonTitle or id
    const term = (searchTerm || '').trim().toLowerCase();
    if (term) {
      arr = arr.filter(q =>
        (q.content || '').toString().toLowerCase().includes(term) ||
        (q.lessonTitle || '').toString().toLowerCase().includes(term) ||
        String(q.id || '').includes(term)
      );
    }

    // lesson filter via dropdown (lessonFilter holds lesson id as string)
    if (lessonFilter) {
      const n = lessonFilter;
      arr = arr.filter(q => String(q.lessonId) === String(n) || String(q.lessonId) === String((q.lesson || {}) .id) );
    }

    // expiration date filter (YYYY-MM-DD)
    if (expirationFilter) {
      arr = arr.filter(q => {
        const d = q.expirationDate ? String(q.expirationDate).slice(0, 10) : '';
        return d === expirationFilter;
      });
    }

    // sort by comparator
    arr.sort(comparator);

    return arr;
  }, [questions, searchTerm, lessonFilter, expirationFilter, orderBy, orderDir]);

  // pagination slice
  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return processed.slice(start, start + rowsPerPage);
  }, [processed, page, rowsPerPage]);

  const handleRequestSort = (property) => {
    if (orderBy === property) {
      setOrderDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(property);
      setOrderDir('desc'); // default newest first
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }} dir="rtl">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <BackButton toFallback="/dashboard" />
        <Typography variant="h5">مدیریت سؤال‌ها</Typography>
      </Box>

      {/* filters / search */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          label="جست‌وجو (متن، عنوان درس، شناسه)"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
          sx={{ minWidth: { xs: 180, sm: 260 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        />

        {/* lesson dropdown */}
        <TextField
          select
          size="small"
          label="انتخاب درس"
          value={lessonFilter}
          onChange={e => { setLessonFilter(e.target.value); setPage(0); }}
          sx={{ width: { xs: '48%', sm: 220 } }}
        >
          <MenuItem value="">همه دروس</MenuItem>
          {lessonsLoading ? (
            <MenuItem disabled>در حال بارگذاری...</MenuItem>
          ) : lessons.length === 0 ? (
            <MenuItem disabled>درسی یافت نشد</MenuItem>
          ) : lessons.map(l => (
            <MenuItem key={l.id ?? l.title} value={String(l.id)}>
              {l.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="تاریخ انقضا"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={expirationFilter}
          onChange={e => { setExpirationFilter(e.target.value); setPage(0); }}
          sx={{ width: { xs: '48%', sm: 180 } }}
        />
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => {
          setSearchTerm(''); setLessonFilter(''); setExpirationFilter('');
          setOrderBy('createdAt'); setOrderDir('desc'); setPage(0);
        }}>پاک کردن فیلترها</Button>
      </Box>

      {/* table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer sx={{ overflowX: 'auto', maxHeight: { xs: '60vh', sm: '70vh' } }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>شناسه</TableCell>

                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <TableSortLabel
                      active={orderBy === 'createdAt'}
                      direction={orderBy === 'createdAt' ? orderDir : 'desc'}
                      onClick={() => handleRequestSort('createdAt')}
                    >
                      تاریخ ایجاد
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>متن سؤال</TableCell>

                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <TableSortLabel
                      active={orderBy === 'expirationDate'}
                      direction={orderBy === 'expirationDate' ? orderDir : 'desc'}
                      onClick={() => handleRequestSort('expirationDate')}
                    >
                      انقضا
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>درس</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginated.map(q => (
                  <TableRow key={q.id} hover>
                    <TableCell>{q.id}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{(q.createdAt || '').toString().slice(0, 19).replace('T', ' ') || '—'}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: { xs: 240, sm: 420 },
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 3, sm: 1 },
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {q.content}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{q.expirationDate ? String(q.expirationDate).slice(0, 10) : '—'}</TableCell>
                    <TableCell sx={{ minWidth: { xs: 120, sm: 160 } }}>
                      {q.lessonTitle || (() => {
                        const found = lessons.find(l => String(l.id) === String(q.lessonId));
                        return found ? found.title : `#${q.lessonId ?? '—'}`;
                      })()}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 88 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(q.id)}
                        sx={{ p: { xs: 0.5, sm: 0.25 }, '& svg': { fontSize: { xs: 20, sm: 18 } } }}
                        aria-label="ویرایش سوال"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(q.id)}
                        sx={{ p: { xs: 0.5, sm: 0.25 }, '& svg': { fontSize: { xs: 20, sm: 18 } } }}
                        aria-label="حذف سوال"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        هیچ سؤالی مطابق فیلتر یافت نشد.
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={processed.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{
              '.MuiTablePagination-toolbar': { px: { xs: 1, sm: 2 } },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default QuestionListPage;